import React, { useState, useMemo } from 'react';

/**
 * Componente Agnóstico de Tabela para o CRM PTS
 * @param {Array} columns - Definição das colunas [{ header: 'Nome', accessor: 'nome' }]
 * @param {Array} data - Os dados reais puxados do Firebase
 * @param {Function} onRowClick - (Opcional) Ação ao clicar em uma linha
 * @param {String} emptyMessage - Mensagem caso não haja dados
 */
const TabelaCRM = ({ columns, data, onRowClick, emptyMessage = "Nenhum registro encontrado." }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // LÓGICA 1: Busca Universal (Filtra em todas as colunas dinamicamente)
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        
        return data.filter(item => {
            return Object.values(item).some(val => 
                String(val).toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
    }, [data, searchTerm]);

    // LÓGICA 2: Paginação Local
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredData, currentPage]);

    return (
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            
            {/* CABEÇALHO DA TABELA: Barra de Busca */}
            <div style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                <input 
                    type="text" 
                    placeholder="Pesquisar..." 
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // Reseta a página ao buscar
                    }}
                    style={{
                        padding: '10px 15px', borderRadius: '8px', border: '1px solid #cbd5e1', width: '300px',
                        outline: 'none', fontSize: '0.9rem', color: '#0f172a'
                    }}
                />
                <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>
                    Total: {filteredData.length} registros
                </span>
            </div>

            {/* CORPO DA TABELA */}
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                        <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                            {columns.map((col, index) => (
                                <th key={index} style={{ padding: '15px 20px', fontSize: '0.85rem', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {col.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                    {emptyMessage}
                                </td>
                            </tr>
                        ) : (
                            paginatedData.map((row, rowIndex) => (
                                <tr 
                                    key={row.id || rowIndex} 
                                    onClick={() => onRowClick && onRowClick(row)}
                                    style={{ 
                                        borderBottom: '1px solid #e2e8f0', 
                                        transition: 'background 0.2s',
                                        cursor: onRowClick ? 'pointer' : 'default',
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                    {columns.map((col, colIndex) => (
                                        <td key={colIndex} style={{ padding: '15px 20px', fontSize: '0.95rem', color: '#0f172a' }}>
                                            {/* Permite renderização customizada se o pai enviar, senão mostra o valor bruto */}
                                            {col.render ? col.render(row) : row[col.accessor]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* RODAPÉ DA TABELA: Paginação */}
            {totalPages > 1 && (
                <div style={{ padding: '15px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                    <button 
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: currentPage === 1 ? '#e2e8f0' : '#fff', color: currentPage === 1 ? '#94a3b8' : '#0f172a', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: '600' }}
                    >
                        Anterior
                    </button>
                    <span style={{ fontSize: '0.9rem', color: '#475569', fontWeight: '500' }}>
                        Página {currentPage} de {totalPages}
                    </span>
                    <button 
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: currentPage === totalPages ? '#e2e8f0' : '#fff', color: currentPage === totalPages ? '#94a3b8' : '#0f172a', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: '600' }}
                    >
                        Próxima
                    </button>
                </div>
            )}
        </div>
    );
};

export default TabelaCRM;